import { useEffect, useState } from "react";
import { MapPin, Pencil, Camera, Trash2, Eye, ChevronRight } from "lucide-react";
import type { QuizQuestion } from "@shared/types";
import { useUpdateProfile, useInvalidateProfile } from "../lib/api";
import { ProfileImage } from "./ProfileImage";
import { ProfileImageEditor } from "./ProfileImageEditor";
import { PublicProfileModal } from "./PublicProfileModal";
import { Sheet } from "./ux/Sheet";
import DateWheelPicker from "./quiz/stepper/DateWheelPicker";
import CitySelect from "./quiz/CitySelect";
import { getAge } from "../utils/dateUtils";

/** The read API returns birthday with slashes; the wheel + write want dashes. */
const toDashes = (v?: string | null) => (v ? v.replace(/\//g, "-") : "");

/**
 * The editable identity header — photo, name, then age · pronouns · city.
 * The photo carries floating edit (bottom-right) and delete (top-right) actions
 * like the picture dialog; a "Preview" button shows how the profile looks to
 * others. Name and birthday are edited in the "Your details" sheet; city opens
 * in its own popover when pressed; identity (pronouns/gender/…) is in Basic info.
 */
export function ProfileIdentityHeader({
  userId,
  firstName,
  lastName,
  profileImageUrl,
  birthday,
  cityId,
  cityName,
  pronounLabels,
}: {
  userId?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  profileImageUrl?: string | null;
  birthday?: string | null;
  cityId?: string | null;
  cityName?: string | null;
  pronounLabels: string[];
}) {
  const updateProfile = useUpdateProfile();
  const invalidate = useInvalidateProfile();
  const save = (patch: Parameters<typeof updateProfile.mutate>[0]) =>
    updateProfile.mutate(patch, { onSuccess: () => invalidate() });

  const [photoOpen, setPhotoOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [cityOpen, setCityOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Name + birthday share the "details" sheet; city opens in its own popover.
  const [firstDraft, setFirstDraft] = useState(firstName ?? "");
  const [lastDraft, setLastDraft] = useState(lastName ?? "");
  const [bdayDraft, setBdayDraft] = useState(toDashes(birthday));
  useEffect(() => {
    if (detailsOpen) {
      setFirstDraft(firstName ?? "");
      setLastDraft(lastName ?? "");
      setBdayDraft(toDashes(birthday));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailsOpen]);

  const closeDetails = () => {
    const f = firstDraft.trim();
    const l = lastDraft.trim();
    const patch: Record<string, unknown> = {};
    if (f && (f !== (firstName ?? "") || l !== (lastName ?? ""))) {
      patch.firstName = f;
      patch.lastName = l;
    }
    if (bdayDraft && bdayDraft !== toDashes(birthday)) {
      patch.quizAnswers = { birthday: bdayDraft };
    }
    if (Object.keys(patch).length) save(patch as any);
    setDetailsOpen(false);
  };

  const age = getAge(birthday);

  return (
    <div className="p-4 relative overflow-hidden">
      {/* Preview — how others see this profile (mirrors the settings gear) */}
      {userId && (
        <button
          type="button"
          onClick={() => setPreviewOpen(true)}
          aria-label="Preview your profile"
          className="btn btn-ghost !px-2 absolute top-0 left-0 z-10 inline-flex items-center gap-1.5"
        >
          <Eye className="w-5 h-5" />
          <span className="text-sm">Preview</span>
        </button>
      )}

      <div className="flex flex-col items-center text-center">
        {/* Photo — the whole avatar opens the editor; floating edit (bottom-right)
            and delete (top-right) actions sit on top as separate targets. */}
        <div className="relative w-40 h-40 group">
          <ProfileImage
            size="xxl"
            onClick={() => setPhotoOpen(true)}
            ariaLabel={profileImageUrl ? "Change profile picture" : "Add a profile picture"}
            user={{ firstName, lastName, profileImageUrl }}
          />

          {/* Hover affordance — the entire picture is the click target */}
          <span className="pointer-events-none absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-sm font-medium text-white">
            Change photo
          </span>

          {profileImageUrl && (
            <button
              type="button"
              onClick={() => save({ profileImageUrl: "" })}
              aria-label="Remove profile picture"
              className="absolute top-1 right-1 w-9 h-9 rounded-full bg-white dark:bg-neutral-800 shadow-md border border-black/5 dark:border-white/10 flex items-center justify-center text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}

          <button
            type="button"
            onClick={() => setPhotoOpen(true)}
            aria-label={profileImageUrl ? "Change profile picture" : "Add a profile picture"}
            className="absolute bottom-1 right-1 w-9 h-9 rounded-full bg-primary-500 text-white shadow-md flex items-center justify-center hover:bg-primary-600 transition-colors"
          >
            <Camera className="w-4 h-4" />
          </button>
        </div>

        {/* Name, age — tap to edit name, birthday + city */}
        <button
          type="button"
          onClick={() => setDetailsOpen(true)}
          aria-label="Edit name, birthday and city"
          className="mt-4 inline-flex items-center gap-1.5 group"
        >
          <h1 className="text-2xl md:text-3xl font-bold dark:text-neutral-100">
            {firstName} {lastName}
            {age != null && <span className="font-normal text-neutral-500 dark:text-neutral-400">, {age}</span>}
          </h1>
          <Pencil className="w-4 h-4 text-neutral-400 group-hover:text-primary-500 transition-colors" />
        </button>

        {/* Sub-line — pronouns (edited in Basic info), city (tap) */}
        <div className="mt-2 flex flex-wrap items-center justify-center gap-x-2.5 gap-y-1 text-sm text-neutral-600 dark:text-neutral-300">
          {pronounLabels.length > 0 && <span>{pronounLabels.join(" / ")}</span>}
          {pronounLabels.length > 0 && (
            <span className="text-neutral-300 dark:text-neutral-600">·</span>
          )}
          <button
            type="button"
            onClick={() => setCityOpen(true)}
            aria-label="Edit city"
            className="flex items-center gap-1.5 hover:text-primary-500 transition-colors"
          >
            <MapPin className="w-4 h-4 text-primary-500" />
            {cityName || "Add city"}
          </button>
        </div>
      </div>

      <ProfileImageEditor isOpen={photoOpen} onClose={() => setPhotoOpen(false)} />

      <PublicProfileModal
        userId={userId ?? null}
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
        fresh
      />

      <Sheet open={detailsOpen} onClose={closeDetails} title="Your details">
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="input-label">First name</label>
              <input
                className="input-glass"
                value={firstDraft}
                onChange={(e) => setFirstDraft(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <label className="input-label">Last name</label>
              <input
                className="input-glass"
                value={lastDraft}
                onChange={(e) => setLastDraft(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="input-label">Birthday</label>
            <DateWheelPicker value={bdayDraft || undefined} onChange={setBdayDraft} />
          </div>
          <div className="space-y-1.5">
            <label className="input-label">City</label>
            <button
              type="button"
              onClick={() => setCityOpen(true)}
              className="input-glass-wrapper w-full flex items-center justify-between gap-3 text-left hover:border-primary-400 dark:hover:border-primary-500 transition-colors"
            >
              <span
                className={
                  cityName
                    ? "truncate text-neutral-900 dark:text-neutral-50"
                    : "text-neutral-400 dark:text-neutral-500"
                }
              >
                {cityName || "Select"}
              </span>
              <ChevronRight className="w-4 h-4 text-neutral-400 flex-shrink-0" />
            </button>
          </div>
        </div>
      </Sheet>

      {/* City — opens in its own popover from the sub-line or the details sheet */}
      <Sheet open={cityOpen} onClose={() => setCityOpen(false)} title="City">
        <CitySelect
          question={{} as QuizQuestion}
          value={cityId ?? ""}
          limit={500}
          onChange={(id) => {
            save({ quizAnswers: { location: id } as any });
            setCityOpen(false);
          }}
        />
      </Sheet>
    </div>
  );
}
